/**
 * L'ANTEPRIMA animata di una clip nel selettore di «Le mie emote» (I-618 punto 4): la parte pura, senza React e senza rete.
 *
 * Qavatar manda, per ogni candidata `ready`, `preview: { rgbUrl, alphaUrl, posterUrl, durationMs }` (contratto 2026-10-11, QAvatar#39).
 * Le `pending` e le `failed` non l'hanno. Qui si legge in modo DIFENSIVO: una preview che non si legge vale «assente», mai un'eccezione e
 * mai un motivo per scartare la candidata. (Senza import: `avatarApi` importa questo file, non il contrario.)
 */
export interface AnteprimaEmote {
  rgbUrl: string;
  alphaUrl: string;
  posterUrl: string;
  /** Intero in (0, 10000]. */
  durationMs: number;
}

/** Quante tessere possono avere il video acceso insieme: 2 `<video>` ciascuna (colore + alfa) = 6 lettori al massimo. */
export const TETTO_ANTEPRIME_VIDEO = 3;
/**
 * Il tetto della `preview.durationMs`: 10 s, come la ricetta `v2-10s` di QAvatar (ADR-0220, scelta di Luca «tetto 0220: 10 s»).
 * Con 5000 una preview piu' lunga valeva «assente» e l'anteprima animata di una clip `v2-10s` restava il poster.
 */
export const ANTEPRIMA_DURATA_MAX_MS = 10000;
/** «accorciata a N s» si scrive solo se la preview e' piu' corta della clip di OLTRE questo (ADR-0220 §2, selettore). */
export const ACCORCIATA_SOGLIA_MS = 500;

const urlHttp = (v: unknown): v is string => {
  if (typeof v !== 'string' || v.length === 0 || v.length > 2048) return false;
  try {
    const p = new URL(v).protocol;
    return p === 'https:' || p === 'http:';
  } catch {
    return false;
  }
};

/** La `preview` di una candidata, o `undefined` se manca o non si legge (URL non http(s), durata non intera in (0, 10000]). */
export function leggiAnteprima(raw: unknown): AnteprimaEmote | undefined {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return undefined;
  const { rgbUrl, alphaUrl, posterUrl, durationMs } = raw as Record<string, unknown>;
  if (!urlHttp(rgbUrl) || !urlHttp(alphaUrl) || !urlHttp(posterUrl)) return undefined;
  if (typeof durationMs !== 'number' || !Number.isInteger(durationMs) || durationMs <= 0 || durationMs > ANTEPRIMA_DURATA_MAX_MS) return undefined;
  return { rgbUrl, alphaUrl, posterUrl, durationMs };
}

/** Le tessere che fanno girare il video: pronte, con una preview, VISIBILI, le prime `tetto` nell'ordine dell'elenco. Movimento ridotto: nessuna. */
export function conVideo(
  candidate: ReadonlyArray<{ clipId: string; emoteStatus: string; preview?: AnteprimaEmote }>,
  visibili: ReadonlySet<string>,
  ridotto: boolean,
  tetto: number = TETTO_ANTEPRIME_VIDEO,
): Set<string> {
  const scelte = new Set<string>();
  if (ridotto) return scelte;
  for (const c of candidate) {
    if (scelte.size >= tetto) break;
    if (c.emoteStatus === 'ready' && c.preview && visibili.has(c.clipId)) scelte.add(c.clipId);
  }
  return scelte;
}

/** La durata mostrata nel selettore: secondi INTERI, arrotondati al piu' vicino (500 ms sale), mai meno di 1. */
export const secondiAnteprima = (ms: number): number => Math.max(1, Math.round(ms / 1000));

/**
 * N di «accorciata a N s», o `null`: serve `durationSec` (facoltativo, senza resta solo la durata) e la preview deve essere piu' corta
 * della clip di OLTRE 500 ms (501 si', 500 no). N e' la durata dell'anteprima, arrotondata come `secondiAnteprima`.
 */
export function accorciataA(preview: Pick<AnteprimaEmote, 'durationMs'>, durationSec: number | undefined): number | null {
  if (typeof durationSec !== 'number' || !Number.isFinite(durationSec)) return null;
  return durationSec * 1000 - preview.durationMs > ACCORCIATA_SOGLIA_MS ? secondiAnteprima(preview.durationMs) : null;
}
